import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button, Input } from '../design-system';
import AuthLayout, { AuthPasswordInput } from '../components/AuthLayout';
import { ADMIN_BRAND } from '../components/authBrand';
import { sanitizeOtp, isCompleteOtp, OTP_LENGTH } from '../lib/otpInput';
import { getDeviceId } from '../lib/deviceId';

const AUTH_SERVICE_URL = process.env.REACT_APP_AUTH_SERVICE_URL || 'http://localhost:4000';

// Two-step recovery: ask for the email, then the emailed code plus a new
// password. The code is random per request, hashed at rest, single-use and
// expires in 10 minutes — see auth-service src/utils/otp.js.
//
// An unregistered address is now told so outright, rather than being given a
// vague "if that email is registered". Signup already reveals which addresses
// exist (409 on a taken one), so the coyness here bought nothing and cost the
// user any way of telling a typo from a forgotten password. What limits
// enumeration is the per-device rate limit, not the wording.

// Keys the recovery rate limit per browser rather than per IP. This page uses
// raw axios, which has no interceptor, so the header goes on by hand.
const deviceHeader = () => {
  const id = getDeviceId();
  return id ? { 'X-Device-Id': id } : {};
};
function ForgotPassword() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [sentTo, setSentTo] = useState('');
  const [loading, setLoading] = useState(false);

  const requestOtp = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const { data } = await axios.post(
        `${AUTH_SERVICE_URL}/auth/forgot-password`,
        { email },
        // Raw axios here, not axiosClient — no interceptor, so the header that
        // keys the recovery rate limit per browser goes on by hand.
        { headers: deviceHeader() }
      );
      setSentTo(data?.message || '');
      setStep(2);
    } catch (err) {
      setError(err.response?.data?.error || 'Something went wrong — try again');
    } finally {
      setLoading(false);
    }
  };

  const submitReset = async (e) => {
    e.preventDefault();
    setError('');
    if (newPassword !== confirm) {
      setError('Passwords do not match');
      return;
    }
    setLoading(true);
    try {
      await axios.post(
        `${AUTH_SERVICE_URL}/auth/reset-password`,
        { email, otp, newPassword },
        { headers: deviceHeader() }
      );
      navigate('/login', { replace: true });
    } catch (err) {
      setError(err.response?.data?.error || 'Reset failed — check the OTP and try again');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout
      {...ADMIN_BRAND}
      title={step === 1 ? 'Reset your password' : 'Enter your OTP'}
      subtitle={
        step === 1
          ? "Enter your account email and we'll send a one-time code"
          : sentTo || `Enter the code sent for ${email}, then pick a new password`
      }
      error={error}
      footer={<Link to="/login">← Back to sign in</Link>}
    >
      {step === 1 ? (
        <form onSubmit={requestOtp} className="auth-form">
          <Input
            label="Email"
            id="fp-email"
            type="email"
            placeholder="you@firm.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            disabled={loading}
            autoComplete="email"
          />
          <Button type="submit" variant="primary" size="lg" loading={loading} className="auth-submit">
            Send OTP
          </Button>
        </form>
      ) : (
        <form onSubmit={submitReset} className="auth-form">
          <div className="auth-otp">
            <Input
              label="One-time code"
              id="fp-otp"
              inputMode="numeric"
              placeholder="••••••"
              value={otp}
              onChange={(e) => setOtp(sanitizeOtp(e.target.value))}
              maxLength={OTP_LENGTH}
              required
              disabled={loading}
              autoComplete="one-time-code"
            />
          </div>
          <AuthPasswordInput
            label="New password"
            id="fp-new"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            required
            disabled={loading}
            autoComplete="new-password"
            helperText="At least 10 characters with upper, lower, digit and special."
          />
          <Input
            label="Confirm new password"
            id="fp-confirm"
            type="password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            required
            disabled={loading}
            autoComplete="new-password"
          />
          <Button
            type="submit"
            variant="primary"
            size="lg"
            loading={loading}
            className="auth-submit"
            disabled={!isCompleteOtp(otp)}
          >
            Reset Password
          </Button>
        </form>
      )}
    </AuthLayout>
  );
}

export default ForgotPassword;
